import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer as DeckHeatmapLayer } from "@deck.gl/aggregation-layers";
import LinearProgress from "@mui/material/LinearProgress";
import { useLocation } from "react-router-dom";
import { HyperTrackHome } from "../../../API/HyperTrack/HomePage";

interface AppHeatMapProps {
  startDate: Date | null;
  endDate: Date | null;
  city: string | null;
  merchant: string | null;
}

interface HeatmapPoint {
  lat: number;
  lng: number;
  weight?: number;
}

const AppHeatMapComponent: React.FC<AppHeatMapProps> = ({
  startDate,
  endDate,
  city,
  merchant,
}) => {
  const location = useLocation();

  const isNewFleetDemandRoute = location.pathname.includes(
    "/NewFleetDemandForm"
  );

  const containerStyle = useMemo(
    () => ({
      width: "100%",
      height: isNewFleetDemandRoute ? "44rem" : "16.5rem",
    }),
    [isNewFleetDemandRoute]
  );

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [heatmapData, setHeatmapData] = useState<HeatmapPoint[]>([]);
  const [loading, setLoading] = useState(false);

  const overlayRef = useRef<GoogleMapsOverlay | null>(null);

  const calculateHeatmapCenter = (
    data: HeatmapPoint[]
  ): google.maps.LatLngLiteral | undefined => {
    if (data.length === 0) return undefined;

    const sum = data.reduce(
      (acc, point) => ({
        lat: acc.lat + point.lat,
        lng: acc.lng + point.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / data.length,
      lng: sum.lng / data.length,
    };
  };

  const center = calculateHeatmapCenter(heatmapData);

  useEffect(() => {
    let isActive = true;

    const fetchHeatmapData = async () => {
      try {
        setLoading(true);

        const data =
          await HyperTrackHome.api.googleMapsOnDutyRidersHyperTrack(
            startDate,
            endDate,
            merchant,
            city
          );

        if (!isActive) return;

        const points: HeatmapPoint[] = (data || [])
          .filter(
            (e: any) =>
              e.lat !== null &&
              e.lat !== undefined &&
              e.lng !== null &&
              e.lng !== undefined
          )
          .map((e: any) => ({
            lat: parseFloat(e.lat.toString()),
            lng: parseFloat(e.lng.toString()),
            weight: 1,
          }))
          .filter((e) => !Number.isNaN(e.lat) && !Number.isNaN(e.lng));

        setHeatmapData(points);
      } catch (error) {
        console.error("Heatmap data fetch failed:", error);
        setHeatmapData([]);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchHeatmapData();

    return () => {
      isActive = false;
    };
  }, [startDate, endDate, city, merchant]);

  useEffect(() => {
    if (!map) return;

    if (heatmapData.length === 0) {
      overlayRef.current?.setProps({
        layers: [],
      });
      return;
    }

    const heatmapLayer = new DeckHeatmapLayer<HeatmapPoint>({
      id: "hypertrack-riders-heatmap",
      data: heatmapData,
      getPosition: (d) => [d.lng, d.lat],
      getWeight: (d) => d.weight || 1,

      radiusPixels: 40,
      intensity: 1,
      threshold: 0.05,
      opacity: 0.8,
    });

    if (!overlayRef.current) {
      overlayRef.current = new GoogleMapsOverlay({
        interleaved: false,
        layers: [heatmapLayer],
      });

      overlayRef.current.setMap(map);
    } else {
      overlayRef.current.setProps({
        layers: [heatmapLayer],
      });
    }

    const mapCenter = calculateHeatmapCenter(heatmapData);
    if (mapCenter) {
      map.setCenter(mapCenter);
    }
  }, [map, heatmapData]);

  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, []);

  const onLoad = (mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  };

  const onUnmount = () => {
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }

    setMap(null);
  };

  return (
    <>
      {loading ? (
        <div className="w-full">
          <LinearProgress
            sx={{
              backgroundColor: "white",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00CD5B",
              },
            }}
          />
        </div>
      ) : heatmapData.length > 0 && center ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          zoom={8}
          options={{
            fullscreenControl: true,
            zoomControl: true,
          }}
          center={center}
          onLoad={onLoad}
          onUnmount={onUnmount}
        />
      ) : (
        <div className="w-full" style={{ height: containerStyle.height }}>
          No heatmap data found
        </div>
      )}
    </>
  );
};

export const AppHeatMap = React.memo(AppHeatMapComponent);